import React, { useState } from 'react';

import Link from 'next/link';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import { API_URL } from '@/lib/consts';

interface WelcomeCardProps {
  onDismiss: () => void;
}

export const WelcomeCard: React.FC<WelcomeCardProps> = ({ onDismiss }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleGotIt = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/user/mark_welcomed`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        // Only dismiss if the backend confirms success
        onDismiss();
      } else {
        console.error('Failed to mark user as welcomed, status:', response.status);
        // Show error to user instead of silently dismissing
        // TODO(2): Remove alert after mark-user-welcomed has been stable for a while
        alert('Failed to save your preference. Please try again.');
      }
    } catch (error) {
      console.error('Error marking user as welcomed:', error);
      // Show error to user instead of silently dismissing
      // TODO(2): Remove alert after mark-user-welcomed has been stable for a while
      alert('Connection error. Please check your internet and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 3,
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Welcome! Start here.
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          New to the community? Check out our help guide to learn how to get the most out of your
          experience.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Learn how to ask questions, get recommendations, follow topics, and connect with experts.
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button component={Link} href="/help" variant="outlined" size="medium">
          View Help Guide
        </Button>
        <Button onClick={handleGotIt} variant="contained" size="medium" disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Got it'}
        </Button>
      </CardActions>
    </Card>
  );
};
